import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, EmptyState } from '@immo/ui-kit';
import { Link, useLoaderData, useRevalidator, useSearchParams } from 'react-router-dom';
import {
  fleetProfileCapacity,
  isFleetHostCommandVerb,
  type FleetCommandBody,
  type FleetCommandView,
  type FleetLaneCommandVerb,
  type FleetLaneEngine,
  type FleetMachineOverview,
  type FleetProfileCapacity,
  type FleetLogLineStream,
  type FleetLogReadLimit,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useCan, useCurrentAdminName } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ChevronLeftIcon, ClockIcon, ServerIcon } from '@/components/layout/icons';
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query';
import {
  MachineTable,
  MachineTableToolbar,
  LANE_STATE_FILTERS,
  type FleetSelection,
  type LaneStateFilter,
} from '@/components/fleet/machine-table';
import { MachinePanel } from '@/components/fleet/machine-panel';
import { MachineDetail, type ViewerState } from '@/components/fleet/machine-detail';
import { ApprovalDialog, type ApprovalVerb } from '@/components/fleet/approval-dialog';
import { CommandDialog } from '@/components/fleet/command-dialog';
import { RunCommandDialog } from '@/components/fleet/run-command-dialog';
import { E2eUnlockDialog } from '@/components/fleet/e2e-unlock-dialog';
import { SystemPreviewDialog } from '@/components/fleet/system-preview-dialog';
import { EMPTY_ENGINE_FORM, engineBodyFor, engineFormFor, type EngineForm } from '@/components/fleet/engine-form';
import type { ConfirmableVerb, FormCommandVerb, MachineCommandVerb } from '@/components/fleet/command-verbs';
import { useFleetCommands } from '@/hooks/use-fleet-commands';
import { useFleetJobs } from '@/hooks/use-fleet-jobs';
import { groupAndSort } from '@/lib/fleet/machine-order';

const POLL_INTERVAL_MS = 10_000;

const DEFAULT_VIEWER: ViewerState = {
  open: true,
  stream: 'agent',
  limit: 50,
  log: null,
  loading: false,
};

export async function fleetLoader(): Promise<ReadonlyArray<FleetMachineOverview>> {
  const result = await apiClient.adminFleet.overview();

  if (!result.ok) {
    throw new Response('Failed to load fleet', { status: result.status });
  }

  return result.body.data;
}

/** The deep-link parameters: `/fleet?machine=<id>&lane=<profile>`, and the toolbar's `?state=` / `?q=` (OPS-128). */
const MACHINE_PARAM = 'machine';
const LANE_PARAM = 'lane';
const STATE_PARAM = 'state';
const QUERY_PARAM = 'q';

function stateFilterOf(value: string | null): LaneStateFilter {
  return LANE_STATE_FILTERS.find((filter) => filter === value) ?? 'all';
}

type PendingAction = { readonly verb: ApprovalVerb; readonly machineId: string };
/** The open form verb's dialog (`run-command`, `e2e-unlock`, `system-preview`): its machine, and the command it follows once issued. */
type FormCommandState = {
  readonly verb: FormCommandVerb;
  readonly machineId: string;
  readonly commandId: string | null;
};
type PendingCommand = {
  readonly verb: ConfirmableVerb;
  readonly machineId: string;
  /** `null` for a host verb — the confirm then names the machine alone. */
  readonly profile: string | null;
  /** Only read for `verb === 'engine'` — the lane, optional project and engine to pin. */
  readonly engineForm: EngineForm;
  /** Only read for `stop` / `stop-all` (fleet-agent ADR 0013) — let the round in hand finish first. */
  readonly finish: boolean;
};

/** Every field a verb does not read, at its untouched value. */
const PENDING_DEFAULTS = {
  engineForm: EMPTY_ENGINE_FORM,
  finish: false,
} as const;

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

type IssueFailure = { readonly errorCode: string | undefined };

/** The message for a refused command: its error code translated, or the fallback when it has none. */
function refusalMessage(failure: IssueFailure, tErrors: (key: string) => string, fallback: string): string {
  return failure.errorCode === undefined ? fallback : tErrors(failure.errorCode);
}

/**
 * The request body for a confirmed verb. A host verb carries no `profile`
 * key at all — the body schema is `.strict()`, so sending the lane the
 * operator happened to be looking at would be a 400, and rightly so.
 */
function bodyFor(pending: PendingCommand): FleetCommandBody {
  // `finish` travels only when ticked — absent is the default, at the next step boundary.
  if (pending.verb === 'stop-all') return { verb: 'stop-all', ...(pending.finish ? { finish: true } : {}) };

  if (isFleetHostCommandVerb(pending.verb)) return { verb: pending.verb };

  // Host-wide (fleet-agent ADR 0018): the page prunes every project's worktrees, so it names none.
  if (pending.verb === 'system-prune') return { verb: 'system-prune' };

  if (pending.verb === 'stop') {
    return { verb: 'stop', profile: pending.profile as string, ...(pending.finish ? { finish: true } : {}) };
  }

  if (pending.verb === 'engine') return engineBodyFor(pending.engineForm);

  return { verb: pending.verb, profile: pending.profile as string };
}

/**
 * Each lane's fleet-wide cap, seen from the open machine — keyed by lane, and
 * only for lanes that have one. Computed against the whole overview, because a
 * cap is about every machine running the lane, not this one.
 */
function capacitiesFor(
  machines: ReadonlyArray<FleetMachineOverview>,
  selectedId: string | null,
): Readonly<Record<string, FleetProfileCapacity>> {
  const selected = machines.find((machine) => machine.id === selectedId);

  if (selected === undefined) return {};

  const now = new Date(Date.now());
  const entries = selected.profiles.flatMap((profile) => {
    const capacity = fleetProfileCapacity(machines, profile.profile, selected.id, now);

    return capacity === null ? [] : [[profile.profile, capacity] as const];
  });

  return Object.fromEntries(entries);
}

export function FleetPage() {
  const t = useTranslations('pages.admin.fleet');
  const tErrors = useTranslations('errors');
  const data = useLoaderData() as ReadonlyArray<FleetMachineOverview>;
  const revalidator = useRevalidator();
  const canCommand = useCan('backoffice:fleet:command');
  const currentAdminName = useCurrentAdminName();

  // The open machine lives in the URL (`?machine=<id>`), not in component
  // state: an operator diagnosing a host sends the link, and the recipient
  // lands on that machine's panel. Nothing is pre-selected (OPS-67): without
  // the parameter the page is the grid alone, and an id that is unknown or has
  // left the list degrades to the grid rather than to nothing.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get(MACHINE_PARAM);
  const selectedId = data.some((machine) => machine.id === requestedId) ? requestedId : null;
  const requestedLane = searchParams.get(LANE_PARAM);
  // A lane the machine no longer reports degrades to the machine's own view.
  const selectedLane =
    selectedId !== null &&
    data.find((machine) => machine.id === selectedId)?.profiles.some((profile) => profile.profile === requestedLane)
      ? requestedLane
      : null;
  const selection: FleetSelection = selectedId === null ? null : { machineId: selectedId, lane: selectedLane };
  const stateFilter = stateFilterOf(searchParams.get(STATE_PARAM));
  const query = searchParams.get(QUERY_PARAM) ?? '';

  // Desktop: the panel lies over the grid. Below `sm` the open machine is a
  // full-screen level of its own, with the topbar's back chevron standing in
  // for the grid as the statement of where you are.
  const isPhone = useMediaQuery(PHONE_QUERY);

  // Every parameter is written with `replace` so the table does not stack one history entry per click — back should
  // leave the fleet page, not walk the machines in order.
  const patchParams = useCallback(
    (patch: Readonly<Record<string, string | null>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearSelectedId = useCallback(() => patchParams({ [MACHINE_PARAM]: null, [LANE_PARAM]: null }), [patchParams]);
  const selectMachine = useCallback(
    (id: string) => patchParams({ [MACHINE_PARAM]: id, [LANE_PARAM]: null }),
    [patchParams],
  );
  const selectLane = useCallback(
    (id: string, lane: string) => patchParams({ [MACHINE_PARAM]: id, [LANE_PARAM]: lane }),
    [patchParams],
  );

  // The commands feed follows one machine: the open one, else the last one acted on from the table — so a row's
  // icons turn into the step of the verb just pressed on it, with no panel open.
  const [actedId, setActedId] = useState<string | null>(null);
  const commandsMachineId = selectedId ?? actedId;
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [pendingCommand, setPendingCommand] = useState<PendingCommand | null>(null);
  const [commandBusy, setCommandBusy] = useState(false);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [viewers, setViewers] = useState<Readonly<Record<string, ViewerState>>>({});
  const [optimisticCommands, setOptimisticCommands] = useState<ReadonlyArray<FleetCommandView>>([]);
  const [mutationsInFlight, setMutationsInFlight] = useState(0);
  const [formCommand, setFormCommand] = useState<FormCommandState | null>(null);
  const [formCommandBusy, setFormCommandBusy] = useState(false);
  const [formCommandError, setFormCommandError] = useState<string | null>(null);

  const mutationPending = pendingAction !== null || pendingCommand !== null || mutationsInFlight > 0;

  const { commands: polledCommands, refresh: refreshCommands } = useFleetCommands(commandsMachineId, {
    paused: mutationPending,
  });
  const { jobs, refresh: refreshJobs } = useFleetJobs({ paused: mutationPending });

  // Drop an optimistic row the moment the poll has caught up with its id —
  // computed on every render (never via a lagging effect), or the render in
  // between the poll resolving and the effect pruning would carry the same
  // command twice under the same list key.
  useEffect(() => {
    setOptimisticCommands((previous) => previous.filter((row) => !polledCommands.some((row2) => row2.id === row.id)));
  }, [polledCommands]);

  const commands = useMemo(() => {
    const polledIds = new Set(polledCommands.map((command) => command.id));

    return [...optimisticCommands.filter((command) => !polledIds.has(command.id)), ...polledCommands];
  }, [optimisticCommands, polledCommands]);

  // Every viewer is scoped to the selected machine — dropped wholesale on a
  // machine switch, taking any minted download link with it.
  useEffect(() => {
    setViewers({});
    setOptimisticCommands([]);
  }, [commandsMachineId]);

  // 10s poll of the overview, suppressed while a revalidation or a mutation is
  // already in flight, and while the tab is hidden — `stale` and every
  // timestamp on this page are otherwise frozen at load time on an open tab.
  useEffect(() => {
    const interval = window.setInterval(() => {
      if (revalidator.state !== 'idle') return;

      if (mutationPending) return;

      if (document.visibilityState !== 'visible') return;

      revalidator.revalidate();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [revalidator, mutationPending]);

  const { pending, machines } = useMemo(() => groupAndSort(data), [data]);
  const capacities = useMemo(() => capacitiesFor(data, selectedId), [data, selectedId]);

  // Escape closes the panel — but never underneath an open confirm, whose own
  // Escape is the one the operator means.
  useEffect(() => {
    if (selectedId === null || pendingAction !== null || pendingCommand !== null || formCommand !== null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearSelectedId();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedId, pendingAction, pendingCommand, formCommand, clearSelectedId]);
  const selected = data.find((machine) => machine.id === selectedId) ?? null;
  const actionTarget =
    pendingAction !== null ? (data.find((machine) => machine.id === pendingAction.machineId) ?? null) : null;
  const formTarget =
    formCommand !== null ? (data.find((machine) => machine.id === formCommand.machineId) ?? null) : null;
  const commandTarget =
    pendingCommand !== null ? (data.find((machine) => machine.id === pendingCommand.machineId) ?? null) : null;

  const closeDialog = () => {
    if (busy) return;

    setPendingAction(null);
    setActionError(null);
  };

  const confirmAction = async () => {
    if (pendingAction === null) return;

    setBusy(true);
    setActionError(null);

    try {
      const result = await apiClient.adminFleetMachines[pendingAction.verb]({
        pathParams: { machineId: pendingAction.machineId },
      });

      if (result.ok) {
        setPendingAction(null);
        await revalidator.revalidate();

        return;
      }

      const code = errorCodeOf(result.body);
      setActionError(code !== undefined ? tErrors(code) : t('generic-error'));
    } finally {
      setBusy(false);
    }
  };

  function addOptimisticCommand(command: FleetCommandView) {
    setOptimisticCommands((previous) => [command, ...previous]);
  }

  /** Issues one command over `POST …/commands`; `202` adds the optimistic row and reports the new commandId. */
  async function issueCommand(
    machineId: string,
    body: FleetCommandBody,
  ): Promise<{ readonly ok: true; readonly commandId: string } | ({ readonly ok: false } & IssueFailure)> {
    setMutationsInFlight((count) => count + 1);

    try {
      const result = await apiClient.adminFleetCommands.issue({ pathParams: { machineId }, body });

      if (!result.ok) {
        return { ok: false, errorCode: errorCodeOf(result.body) };
      }

      addOptimisticCommand({
        id: result.body.commandId,
        machineId,
        verb: body.verb,
        profile: 'profile' in body ? body.profile : null,
        // This page only ever issues as the signed-in operator; a machine's
        // row arrives from the server on the next list read (IMM-939).
        requestedByKind: 'admin',
        requestedByName: currentAdminName,
        requestedAt: new Date().toISOString(),
        outcome: 'accepted',
        detail: null,
        stream: 'stream' in body ? body.stream : null,
        lines: 'lines' in body ? body.lines : null,
        ticket: body.verb === 'handover' ? body.ticket : null,
        finish: body.verb === 'stop' || body.verb === 'stop-all' ? (body.finish ?? null) : null,
        engine: body.verb === 'engine' ? body.engine : null,
        lane: body.verb === 'engine' ? (body.lane ?? null) : null,
        project:
          body.verb === 'engine' ||
          body.verb === 'start' ||
          body.verb === 'stop' ||
          body.verb === 'restart' ||
          body.verb === 'run-command' ||
          body.verb === 'e2e-unlock' ||
          body.verb === 'system-preview' ||
          body.verb === 'system-prune'
            ? (body.project ?? null)
            : null,
        worker: body.verb === 'e2e-unlock' ? (body.worker ?? null) : null,
        argv: body.verb === 'run-command' ? [...body.argv] : null,
        planModel: body.verb === 'engine' ? (body.planModel ?? null) : null,
        implementModel: body.verb === 'engine' ? (body.implementModel ?? null) : null,
        payload: null,
        downloadUrl: null,
        downloadExpiresInSeconds: null,
        settledAt: null,
      });

      void refreshCommands();

      return { ok: true, commandId: result.body.commandId };
    } finally {
      setMutationsInFlight((count) => count - 1);
    }
  }

  const closeCommandDialog = () => {
    if (commandBusy) return;

    setPendingCommand(null);
    setCommandError(null);
  };

  const confirmCommand = async () => {
    if (pendingCommand === null) return;

    setCommandBusy(true);
    setCommandError(null);

    try {
      const body = bodyFor(pendingCommand);

      const result = await issueCommand(pendingCommand.machineId, body);

      if (!result.ok) {
        setCommandError(refusalMessage(result, tErrors, t('generic-error')));

        return;
      }

      setPendingCommand(null);
    } finally {
      setCommandBusy(false);
    }
  };

  function onFormVerb(machineId: string, verb: FormCommandVerb) {
    setActedId(machineId);
    setFormCommandError(null);
    setFormCommand({ verb, machineId, commandId: null });
  }

  const closeFormCommand = () => {
    if (formCommandBusy) return;

    setFormCommand(null);
    setFormCommandError(null);
  };

  /** Issues the form's command; the dialog then follows it by the id the backend handed out. */
  const submitFormCommand = async (body: FleetCommandBody) => {
    if (formCommand === null) return;

    setFormCommandBusy(true);
    setFormCommandError(null);

    try {
      const result = await issueCommand(formCommand.machineId, body);

      if (!result.ok) {
        setFormCommandError(refusalMessage(result, tErrors, t('generic-error')));

        return;
      }

      setFormCommand({ ...formCommand, commandId: result.commandId });
    } finally {
      setFormCommandBusy(false);
    }
  };

  function onHostVerb(machineId: string, verb: MachineCommandVerb) {
    setActedId(machineId);
    setPendingCommand({ ...PENDING_DEFAULTS, verb, machineId, profile: null });
  }

  function onLaneVerb(machineId: string, profile: string, verb: FleetLaneCommandVerb) {
    setActedId(machineId);
    setPendingCommand({ ...PENDING_DEFAULTS, verb, machineId, profile });
  }

  function onApproval(machineId: string, verb: ApprovalVerb) {
    setPendingAction({ verb, machineId });
  }

  function onFinishChange(finish: boolean) {
    setPendingCommand((previous) => (previous === null ? previous : { ...previous, finish }));
  }

  function onEngine(machineId: string, lane: string | null, current: FleetLaneEngine | null | undefined) {
    setActedId(machineId);
    setPendingCommand({
      ...PENDING_DEFAULTS,
      verb: 'engine',
      machineId,
      profile: null,
      engineForm: engineFormFor(lane, current),
    });
  }

  function onForceUpdateChange(force: boolean) {
    setPendingCommand((previous) =>
      previous === null ? previous : { ...previous, verb: force ? 'force-update-agent' : 'update-agent' },
    );
  }

  function onEngineFormChange(patch: Partial<EngineForm>) {
    setPendingCommand((previous) =>
      previous === null ? previous : { ...previous, engineForm: { ...previous.engineForm, ...patch } },
    );
  }

  function updateViewer(profile: string, patch: Partial<ViewerState>) {
    setViewers((previous) => ({
      ...previous,
      [profile]: { ...(previous[profile] ?? DEFAULT_VIEWER), ...patch },
    }));
  }

  /**
   * Reads the lane's stored lines (IMM-918). Not a command: the fleet agent
   * pushes on every tick, so this answers for a machine that is asleep or
   * unreachable — which is when somebody opens the viewer.
   */
  async function readLog(profile: string, stream: FleetLogLineStream, limit: FleetLogReadLimit) {
    if (selectedId === null) return;

    const worker = workerFor(profile);

    // No worker reported yet means no lines are stored under any name — an
    // empty log, not a failed read.
    if (worker === null) {
      updateViewer(profile, { stream, limit, log: { worker: '', stream, lines: [] }, loading: false });

      return;
    }

    updateViewer(profile, { stream, limit, loading: true });

    const result = await apiClient.adminFleetLogs.read({
      pathParams: { machineId: selectedId },
      query: { worker, stream, limit },
    });

    updateViewer(profile, { loading: false, ...(result.ok ? { log: result.body } : {}) });
  }

  function workerFor(profile: string): string | null {
    const selected = data.find((machine) => machine.id === selectedId);

    return selected?.profiles.find((entry) => entry.profile === profile)?.worker ?? null;
  }

  // The lane panel opens with its log (OPS-128): there is no toggle, the log is what the panel is for. Viewers are
  // dropped on a machine switch above, so a lane opened again reads afresh.
  const openViewerFor = selectedId !== null && selectedLane !== null ? selectedLane : null;

  useEffect(() => {
    if (openViewerFor === null) return;

    updateViewer(openViewerFor, { ...DEFAULT_VIEWER, open: true });
    void readLog(openViewerFor, DEFAULT_VIEWER.stream, DEFAULT_VIEWER.limit);
    // Read once per lane opened; the viewer's own controls re-read after that.
  }, [selectedId, openViewerFor]);

  function onStreamChange(profile: string, stream: FleetLogLineStream) {
    void readLog(profile, stream, viewers[profile]?.limit ?? 50);
  }

  function onLimitChange(profile: string, limit: FleetLogReadLimit) {
    void readLog(profile, viewers[profile]?.stream ?? 'agent', limit);
  }

  function onRefreshViewer(profile: string) {
    const viewer = viewers[profile];
    void readLog(profile, viewer?.stream ?? 'agent', viewer?.limit ?? 50);
  }

  async function onCleanLogs(profile: string) {
    if (selectedId === null) return;

    const worker = workerFor(profile);

    if (worker === null) return;

    const result = await apiClient.adminFleetLogs.clean({
      pathParams: { machineId: selectedId },
      body: { worker },
    });

    // Re-read rather than trusting the count: the clean removed rows older
    // than a cutoff, and what is left is what the viewer should now show.
    if (result.ok && (viewers[profile]?.open ?? false)) onRefreshViewer(profile);
  }

  const historyButton = (
    <Button size="sm" variant="secondary" asChild>
      <Link to="/fleet/history">
        <ClockIcon className="h-4 w-4" aria-hidden />
        {t('history-cta')}
      </Link>
    </Button>
  );

  if (data.length === 0) {
    return (
      <>
        <PageTopbar leading={<ListLeading title={t('title')} count={0} icon={ServerIcon} />} trailing={historyButton} />
        <div className="flex h-full min-h-0 items-center justify-center">
          <EmptyState
            icon={<ServerIcon className="h-8 w-8" aria-hidden />}
            title={t('empty-heading')}
            description={t('empty-body')}
          />
        </div>
      </>
    );
  }

  const detail =
    selected !== null ? (
      <MachineDetail
        machine={selected}
        lane={selectedLane}
        canCommand={canCommand}
        commands={commands.filter((command) => command.machineId === selected.id)}
        {...(selectedLane !== null && viewers[selectedLane] !== undefined ? { viewer: viewers[selectedLane] } : {})}
        capacities={capacities}
        onStreamChange={onStreamChange}
        onLimitChange={onLimitChange}
        onRefreshViewer={onRefreshViewer}
        onCleanLogs={onCleanLogs}
        jobs={jobs}
        onJobsChanged={refreshJobs}
      />
    ) : null;
  const panelTitle = selected === null ? '' : (selectedLane ?? selected.name);

  return (
    <>
      <PageTopbar
        trailing={historyButton}
        leading={
          isPhone && selected !== null ? (
            <BackLeading
              title={selectedLane === null ? selected.name : `${selected.name} · ${selectedLane}`}
              label={t('back-to-index')}
              onBack={clearSelectedId}
            />
          ) : (
            <ListLeading title={t('title')} count={data.length} icon={ServerIcon} />
          )
        }
      />
      {isPhone && detail !== null ? (
        <div className="h-full min-h-0 overflow-y-auto bg-canvas">{detail}</div>
      ) : (
        <div className="relative h-full min-h-0">
          <div className="flex h-full flex-col gap-4 overflow-y-auto bg-canvas p-4 sm:p-6">
            <MachineTableToolbar
              machines={data}
              state={stateFilter}
              query={query}
              onStateChange={(state) => patchParams({ [STATE_PARAM]: state === 'all' ? null : state })}
              onQueryChange={(value) => patchParams({ [QUERY_PARAM]: value })}
            />
            <MachineTable
              pending={pending}
              machines={machines}
              selection={selection}
              canCommand={canCommand}
              commands={commands}
              jobs={jobs}
              stateFilter={stateFilter}
              query={query}
              onSelectMachine={selectMachine}
              onSelectLane={selectLane}
              onApproval={onApproval}
              onHostVerb={onHostVerb}
              onFormVerb={onFormVerb}
              onHostEngine={(machineId) => onEngine(machineId, null, null)}
              onLaneVerb={onLaneVerb}
              onLaneEngine={(machineId, lane, current) => onEngine(machineId, lane, current)}
            />
          </div>
          {selected !== null && (
            <MachinePanel
              name={panelTitle}
              {...(selectedLane !== null ? { subtitle: `${selected.name} · ${selected.hostname}` } : {})}
              onClose={clearSelectedId}
            >
              {detail}
            </MachinePanel>
          )}
        </div>
      )}
      {pendingAction !== null && actionTarget !== null && (
        <ApprovalDialog
          verb={pendingAction.verb}
          busy={busy}
          error={actionError}
          onConfirm={confirmAction}
          onClose={closeDialog}
        />
      )}
      {formCommand !== null && formTarget !== null && (
        <FormCommandDialog
          state={formCommand}
          machine={formTarget}
          command={
            formCommand.commandId === null
              ? null
              : (commands.find((command) => command.id === formCommand.commandId) ?? null)
          }
          busy={formCommandBusy}
          error={formCommandError}
          onSubmit={(body) => void submitFormCommand(body)}
          onAgain={() => setFormCommand({ ...formCommand, commandId: null })}
          onClose={closeFormCommand}
        />
      )}
      {pendingCommand !== null && commandTarget !== null && (
        <CommandDialog
          verb={pendingCommand.verb}
          machineName={commandTarget.name}
          profile={pendingCommand.profile}
          busy={commandBusy}
          error={commandError}
          onConfirm={confirmCommand}
          onClose={closeCommandDialog}
          engineForm={pendingCommand.engineForm}
          onEngineFormChange={onEngineFormChange}
          onForceUpdateChange={onForceUpdateChange}
          finish={pendingCommand.finish}
          onFinishChange={onFinishChange}
        />
      )}
    </>
  );
}

/** The open form verb's dialog; each one shapes its own body, which the page issues and then follows. */
function FormCommandDialog({
  state,
  machine,
  command,
  busy,
  error,
  onSubmit,
  onAgain,
  onClose,
}: {
  readonly state: FormCommandState;
  readonly machine: FleetMachineOverview;
  readonly command: FleetCommandView | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (body: FleetCommandBody) => void;
  readonly onAgain: () => void;
  readonly onClose: () => void;
}) {
  const shared = {
    machineName: machine.name,
    projects: machine.projects ?? [],
    command,
    busy,
    error,
    onAgain,
    onClose,
  };

  switch (state.verb) {
    case 'run-command':
      return (
        <RunCommandDialog
          {...shared}
          onSubmit={(project, argv) => onSubmit({ verb: 'run-command', project, argv: [...argv] })}
        />
      );
    case 'e2e-unlock':
      return (
        <E2eUnlockDialog
          {...shared}
          onSubmit={(project, worker) =>
            onSubmit({
              verb: 'e2e-unlock',
              ...(project === undefined ? {} : { project }),
              ...(worker === undefined ? {} : { worker }),
            })
          }
        />
      );
    case 'system-preview':
      return (
        <SystemPreviewDialog
          {...shared}
          onSubmit={(project) => onSubmit({ verb: 'system-preview', ...(project === undefined ? {} : { project }) })}
        />
      );
  }
}

/**
 * The phone topbar for level two: a back chevron and the machine's name. The
 * chevron clears `?machine=` rather than calling `history.back()` — the grid
 * writes its selection with `replace`, so there is no entry behind it to go
 * back to, and browser-back should still leave the fleet page entirely.
 */
function BackLeading({
  title,
  label,
  onBack,
}: {
  readonly title: string;
  readonly label: string;
  readonly onBack: () => void;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1">
      <button
        type="button"
        onClick={onBack}
        aria-label={label}
        className="-ml-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-text-secondary"
      >
        <ChevronLeftIcon className="h-5 w-5" aria-hidden />
      </button>
      <h1 className="truncate text-base font-semibold text-text-primary">{title}</h1>
    </div>
  );
}
